import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { amazonConnectApi } from '../../api/amazonconnect';

const OAUTH_SESSION_KEY = 'amazon_oauth_session';

type OAuthSession = {
  connection_id: string;
  redirect_uri: string;
};

type CallbackPhase =
  | 'waiting'
  | 'validating'
  | 'exchanging'
  | 'verifying'
  | 'connected'
  | 'failed';

const PHASE_LABEL: Record<CallbackPhase, string> = {
  waiting: 'Waiting for Amazon…',
  validating: 'Validating authorization…',
  exchanging: 'Exchanging token…',
  verifying: 'Verifying seller…',
  connected: 'Connected',
  failed: 'Failed',
};

export function AmazonOAuthCallbackPage() {
  const [params] = useSearchParams();
  const [phase, setPhase] = useState<CallbackPhase>('waiting');
  const [detail, setDetail] = useState(PHASE_LABEL.waiting);

  useEffect(() => {
    const run = async () => {
      const code = params.get('spapi_oauth_code') ?? params.get('code');
      const state = params.get('state');
      const sellingPartnerId = params.get('selling_partner_id');
      const amazonError = params.get('error') ?? params.get('error_description');

      if (amazonError) {
        setPhase('failed');
        setDetail(`Amazon rejected authorization: ${amazonError}`);
        return;
      }

      if (!code || !state) {
        setPhase('failed');
        setDetail(
          'Missing authorization code from Amazon. Complete sign-in and Approve on Seller Central — do not stay on the consent URL. After approve, Amazon redirects here with spapi_oauth_code.',
        );
        return;
      }

      const raw = sessionStorage.getItem(OAUTH_SESSION_KEY) ?? localStorage.getItem(OAUTH_SESSION_KEY);
      let session: Partial<OAuthSession> = {};
      if (raw) {
        try {
          session = JSON.parse(raw) as OAuthSession;
        } catch {
          session = {};
        }
      }

      try {
        setPhase('validating');
        setDetail(PHASE_LABEL.validating);

        setPhase('exchanging');
        setDetail(PHASE_LABEL.exchanging);

        // State alone is enough — backend resolves connection from persisted OAuth state.
        // sessionStorage is optional (blank tab / popup / cross-tab still works).
        const result = await amazonConnectApi.oauthCallback({
          connection_id: session.connection_id ?? '',
          state,
          code,
          redirect_uri: session.redirect_uri ?? defaultOAuthRedirectUri(),
          // Amazon's merchant id. Seller-scoped SP-API calls (catalog listings,
          // reports) are impossible without it, so it must reach the backend.
          selling_partner_id: sellingPartnerId ?? undefined,
        });

        setPhase('verifying');
        setDetail(PHASE_LABEL.verifying);

        if (result.connection_id) {
          try {
            const verify = await amazonConnectApi.sellerVerification(result.connection_id);
            if (verify.success) {
              setPhase('connected');
              setDetail(
                `Connected to ${verify.marketplace_name ?? 'Amazon marketplace'}${verify.country_code ? ` (${verify.country_code})` : ''}.`,
              );
            } else {
              setPhase('connected');
              setDetail(`Connected (${result.status}). Seller: ${verify.message ?? 'pending verification'}.`);
            }
          } catch {
            setPhase('connected');
            setDetail(`Connected (${result.status}). You can return to the dashboard.`);
          }
        } else {
          setPhase('connected');
          setDetail(`Connection status: ${result.status}.`);
        }

        if (sellingPartnerId) {
          setDetail((prev) => `${prev} Merchant ID: ${sellingPartnerId}.`);
        }

        sessionStorage.removeItem(OAUTH_SESSION_KEY);
        localStorage.removeItem(OAUTH_SESSION_KEY);
      } catch (e) {
        setPhase('failed');
        setDetail(e instanceof Error ? e.message : 'OAuth callback failed');
      }
    };
    void run();
  }, [params]);

  return (
    <div className="mx-auto flex min-h-screen max-w-lg flex-col items-center justify-center bg-slate-50 p-6 text-center">
      <h1 className="text-xl font-semibold text-slate-900">Amazon Authorization</h1>
      <div className="mt-4 w-full max-w-xs">
        <div className="mb-2 flex justify-between text-xs text-slate-500">
          {(['validating', 'exchanging', 'verifying', 'connected'] as CallbackPhase[]).map((step) => (
            <span
              key={step}
              className={
                phase === step
                  ? 'font-semibold text-brand-700'
                  : phase === 'failed'
                    ? 'text-slate-400'
                    : ['validating', 'exchanging', 'verifying', 'connected'].indexOf(phase) >=
                        ['validating', 'exchanging', 'verifying', 'connected'].indexOf(step)
                      ? 'text-emerald-600'
                      : 'text-slate-400'
              }
            >
              {step === 'validating' ? 'Validate' : step === 'exchanging' ? 'Token' : step === 'verifying' ? 'Verify' : 'Done'}
            </span>
          ))}
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-200">
          <div
            className={`h-full transition-all duration-500 ${
              phase === 'failed' ? 'bg-red-500' : phase === 'connected' ? 'bg-emerald-500' : 'bg-brand-600'
            }`}
            style={{
              width:
                phase === 'waiting'
                  ? '10%'
                  : phase === 'validating'
                    ? '35%'
                    : phase === 'exchanging'
                      ? '60%'
                      : phase === 'verifying'
                        ? '85%'
                        : phase === 'connected'
                          ? '100%'
                          : '100%',
            }}
          />
        </div>
      </div>
      <p
        className={`mt-4 text-sm ${
          phase === 'failed' ? 'text-red-700' : phase === 'connected' ? 'text-emerald-700' : 'text-slate-600'
        }`}
      >
        {detail}
      </p>
      <Link
        to="/amazon/connect"
        className="mt-6 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
      >
        Back to Amazon Connect
      </Link>
    </div>
  );
}

export function persistOAuthSession(session: OAuthSession) {
  const payload = JSON.stringify(session);
  sessionStorage.setItem(OAUTH_SESSION_KEY, payload);
  localStorage.setItem(OAUTH_SESSION_KEY, payload);
}

export function defaultOAuthRedirectUri() {
  return `${window.location.origin}/amazon/connect/callback`;
}
